import { FeatureAppDefinition, FeatureServices, Logger } from '@feature-hub/core';
import { ReactFeatureApp } from '@feature-hub/react';
import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import { MyDealerService2_1 } from '@volkswagen-onehub/mydealer-service';
import { MyDealerConfigurationFeatureServiceV2 } from '@volkswagen-onehub/mydealer-configuration-service';
import { TrackingManagerV1 } from '@volkswagen-onehub/tracking-service';
import { FeatureappServiceV1 } from '@volkswagen-onehub/featureapp-service';
import { AuthServiceProviderV2 } from '@volkswagen-onehub/authservice';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { ApiClient, Stage } from '@org-avp/avp-hck-dga-customerui-commons';
import { Brand } from '@org-avp/models';
import { PageInfoServiceV1 } from '@volkswagen-onehub/page-info-service';
import { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import { getBrowserLocale, initI18n } from './i18n/i18n';
import { FeatureService } from './enums/FeatureServiceDefinition';
import { InitConfig } from './interfaces/init-config';
import { ApiClientContext } from './contexts/ApiClientContext';
import { FeatureServicesContext } from './contexts/FeatureServicesContext';
import { TrackingServiceContext } from './contexts/TrackingServiceContext';
import { Giveaway } from './pages/giveaway';
import { TrackingService } from './util/tracking/tracking';
import { FaContentServiceV1 } from '@volkswagen-onehub/fa-content-service';
import { ThemeProvider } from '@volkswagen-onehub/components-core';
import { ConfigurationContext } from './contexts/ConfigurationContext';

export interface AvpDgaFeatureAppDependencies extends FeatureServices {
  readonly [FeatureService.LOGGER]?: Logger;
  readonly [FeatureService.LOCALE_SERVICE]: LocaleServiceV1;
  readonly [FeatureService.MYDEALER_SERVICE]: MyDealerService2_1;
  readonly [FeatureService.MYDEALER_CONFIGURATION_SERVICE]: MyDealerConfigurationFeatureServiceV2;
  readonly [FeatureService.TRACKING_SERVICE]: TrackingManagerV1;
  readonly [FeatureService.FEATURE_APP_SERVICE]: FeatureappServiceV1;
  readonly [FeatureService.VW_AUTH_SERVICE]: AuthServiceProviderV2;
  readonly [FeatureService.PAGE_INFO_SERVICE]: PageInfoServiceV1;
  readonly [FeatureService.SERIALIZED_STATE_MANAGER]?: SerializedStateManagerV1;
  readonly [FeatureService.CONTENT_SERVICE]: FaContentServiceV1;
}

const initTracking = (featureServices: AvpDgaFeatureAppDependencies): TrackingService => {
  const trackingManager = featureServices[FeatureService.TRACKING_SERVICE];
  const myDealerConfigService = featureServices[FeatureService.MYDEALER_CONFIGURATION_SERVICE];
  return new TrackingService(trackingManager, myDealerConfigService);
};

const checkIfAuthenticated = (
  featureServices: AvpDgaFeatureAppDependencies,
): Promise<boolean> | undefined => {
  return featureServices[FeatureService.VW_AUTH_SERVICE]?.register('market').isAuthenticated();
};

export const featureAppDefinition: FeatureAppDefinition<
  ReactFeatureApp,
  AvpDgaFeatureAppDependencies,
  InitConfig
> = {
  dependencies: {
    featureServices: {
      [FeatureService.LOCALE_SERVICE]: '^1.0.0',
    },
  },
  optionalDependencies: {
    featureServices: {
      [FeatureService.LOGGER]: '^1.0.0',
      [FeatureService.MYDEALER_SERVICE]: '^2.1.0',
      [FeatureService.MYDEALER_CONFIGURATION_SERVICE]: '^2.2.0',
      [FeatureService.TRACKING_SERVICE]: '^1.0.0',
      [FeatureService.FEATURE_APP_SERVICE]: '^1.5.0',
      [FeatureService.VW_AUTH_SERVICE]: '^2.0.0',
      [FeatureService.PAGE_INFO_SERVICE]: '^1.0.0',
      [FeatureService.SERIALIZED_STATE_MANAGER]: '^1.0.0',
      [FeatureService.CONTENT_SERVICE]: '^1.0.0',
    },
  },
  create: ({ config, featureServices, baseUrl }) => {
    __webpack_public_path__ = `${baseUrl ?? '/assets/feature-app'}/`;

    // Spinner copy uses the browser language (English if that locale is missing).
    // Giveaway locale is applied after the order loads.
    initI18n(getBrowserLocale());
    const trackingService = initTracking(featureServices);

    void checkIfAuthenticated(featureServices)?.then((isAuthenticated) => {
      trackingService.setUserIsAuthenticated(isAuthenticated);
    });

    return {
      render() {
        return (
          <FeatureServicesContext.Provider value={featureServices}>
            <ConfigurationContext.Provider value={config}>
              <TrackingServiceContext.Provider value={trackingService}>
                <ApiClientContext.Provider
                  value={new ApiClient(config?.stage ?? Stage.DEV, Brand.VW)}
                >
                  <ThemeProvider theme="main" variant="v26">
                    <BrowserRouter>
                      <Routes>
                        <Route path="*" element={<Giveaway />} />
                      </Routes>
                    </BrowserRouter>
                  </ThemeProvider>
                </ApiClientContext.Provider>
              </TrackingServiceContext.Provider>
            </ConfigurationContext.Provider>
          </FeatureServicesContext.Provider>
        );
      },
    };
  },
};

export default featureAppDefinition;
